import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Collapse,
  Divider,
  IconButton,
  Link as MUILink,
  Paper,
  Typography,
} from "@mui/material";
import { useEasierTheme } from "datalign-themes";
import { EzrImage } from "ui-widgets";
import EzrVideo from "datalign-module-template/components/ezr-video";
import EzrAudio from "datalign-module-template/components/ezr-audio/EzrAudio";
import RenderField from "components/common/render-field";
import AssetPreviewButton from "components/common/asset-preview-button";
import Footer from "components/sitelet/input-page/metadata-card/footer";

import Input from "models/input/input";
import Asset from "datalign-module-template/models/asset";
import Boundary from "models/geospatial/boundary";
import Point from "models/geospatial/point";

import DatatypeField from "components/common/render-field/datatype-field";
import ArrowAnimation from "datalign-module-template/components/arrow-animation";

const BaseResultCard = ({
  input,
  showStandardMetadata,
  showDatatypeMetadata,
  HandleOpen,
  open,
  boundaries,
  points,
  imageFiles,
  videoFiles,
  audioFiles,
  textFiles,
}: {
  input: Input;
  showStandardMetadata: string[];
  showDatatypeMetadata: string[];
  open: boolean;
  HandleOpen: (openState: boolean) => void;
  boundaries: Boundary[];
  points: Point[];
  imageFiles: Asset[];
  videoFiles: Asset[];
  audioFiles: Asset[];
  textFiles: Asset[];
}) => {
  const theme = useEasierTheme();

  const isNTGovTheme = ["FAF-nt", "nrdata-orig"].includes(
    process.env.REACT_APP_STYLING_THEME ?? "",
  );

  const cardStyles = {
    borderRadius: isNTGovTheme ? 0 : "default",
  };

  const headerStyles = {
    backgroundColor: isNTGovTheme ? "#127cc0" : "inherit",
  };

  const linkColour = isNTGovTheme
    ? theme?.Colour.OnPrimary()
    : theme?.Colour.Primary();
  return (
    <Paper sx={cardStyles}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={1}
        px={2}
        sx={headerStyles}
      >
        <MUILink
          justifyContent="start"
          width="100%"
          color={linkColour}
          mr={1}
          to={`/view/${input.input_id}`}
          component={RouterLink}
          underline="hover"
          sx={{
            wordWrap: "break-word",
          }}
        >
          <Typography
            variant="h3"
            fontWeight={500}
            fontSize="1.42275rem"
            lineHeight={1.2}
            fontFamily="inherit"
          >
            {input.title}
          </Typography>
        </MUILink>
        {!["nrdata-orig"].includes(
          process.env.REACT_APP_STYLING_THEME ?? "",
        ) && (
          <IconButton onClick={() => HandleOpen(!open)}>
            <ArrowAnimation isOpen={open} style={{ color: linkColour }} />
          </IconButton>
        )}
      </Box>
      <Collapse in={open}>
        {showStandardMetadata && showStandardMetadata.length > 0 && (
          <Box
            bgcolor={
              !["nrdata-orig", "FAF-nt"].includes(
                process.env.REACT_APP_STYLING_THEME ?? "",
              )
                ? "rgba(31, 31, 95, 0.05)"
                : undefined
            }
            display="flex"
            flexWrap="wrap"
          >
            {showStandardMetadata
              .filter(
                (key) =>
                  input.metadata[key] &&
                  !JSON.parse(
                    process.env
                      .REACT_APP_STANDARD_METADATA_SEARCH_CARD_OVERRIDE ?? "[]",
                  ).includes(input.metadata[key].field_type),
              )
              .map(
                (key) =>
                  input.metadata[key] && (
                    <RenderField
                      key={`card-metadata-${key}`}
                      label={key}
                      field={input.metadata[key]}
                      boundaries={boundaries}
                      points={points}
                      isSearchCard={true}
                    />
                  ),
              )}
          </Box>
        )}
        {showStandardMetadata && showStandardMetadata.length > 0 && (
          <Divider
            sx={{
              width: "100%",
              borderColor: theme?.Colour.Primary(),
            }}
          />
        )}

        <Box display="flex" flexWrap="wrap">
          {input.project && (
            <RenderField
              key={`card-metadata-project`}
              label={"Project"}
              field={{ field_type: "form-text", data: input.project }}
              boundaries={[]}
              points={[]}
              isSearchCard={true}
            />
          )}
          {input.datatype && <DatatypeField datatype={input.datatype} />}
          {showStandardMetadata &&
            showStandardMetadata.length > 0 &&
            showStandardMetadata
              .filter(
                (key) =>
                  input.metadata[key] &&
                  JSON.parse(
                    process.env
                      .REACT_APP_STANDARD_METADATA_SEARCH_CARD_OVERRIDE ?? "[]",
                  ).includes(input.metadata[key].field_type),
              )
              .map(
                (key) =>
                  input.metadata[key] && (
                    <RenderField
                      key={`card-metadata-${key}`}
                      label={key}
                      field={input.metadata[key]}
                      boundaries={boundaries}
                      points={points}
                      isSearchCard={true}
                    />
                  ),
              )}
          {showDatatypeMetadata &&
            showDatatypeMetadata.length > 0 &&
            showDatatypeMetadata.map(
              (key) =>
                input.metadata[key] && (
                  <RenderField
                    key={`card-metadata-${key}`}
                    label={key}
                    field={input.metadata[key]}
                    boundaries={boundaries}
                    points={points}
                    isSearchCard={true}
                  />
                ),
            )}
        </Box>
        {(imageFiles.length > 0 ||
          videoFiles.length > 0 ||
          audioFiles.length > 0 ||
          textFiles.length > 0) && [
          <Divider
            key={`assets-divider-${input.input_id}`}
            sx={{
              width: "100%",
              borderColor: theme?.Colour.Secondary(),
              my: "4px",
            }}
          />,

          (imageFiles.length > 0 ||
            videoFiles.length > 0 ||
            audioFiles.length > 0) && (
            <Box
              sx={{
                alignContent: "center",
                justifyContent: "center",
                width: "100%",
                m: 1,
              }}
              key={`assets-container-${input.input_id}`}
            >
              {imageFiles.map((file) => (
                <EzrImage
                  key={`preview-image-${file.guid}`}
                  src={file.presigned_url}
                  alt={file.name}
                  loading="lazy"
                  style={{
                    padding: 4,
                    maxWidth: "400px",
                    aspectRatio: "auto",
                    objectPosition: "center",
                    width: "100%",
                    height: "auto",
                    objectFit: "contain",
                    float: "inline-start",
                  }}
                />
              ))}
              {videoFiles.map((file) => (
                <EzrVideo
                  key={`preview-video-${file.guid}`}
                  src={file.presigned_url}
                  controls
                  style={{ maxWidth: "100%", padding: 4 }}
                />
              ))}
              {audioFiles.map((file) => (
                <EzrAudio
                  key={`preview-audio-${file.guid}`}
                  src={file.presigned_url}
                  style={{
                    padding: 4,
                  }}
                  controls
                />
              ))}
            </Box>
          ),
          textFiles.length > 0 && (
            <Box m={1} key={`assets-previews-${input.input_id}`}>
              {textFiles.map((file) => (
                <AssetPreviewButton
                  asset={file}
                  key={`text-preview-${file.guid}`}
                />
              ))}
            </Box>
          ),
        ]}
        <Footer input={input} boundaries={[]} points={[]} searchCard={true} />
      </Collapse>
    </Paper>
  );
};

export default BaseResultCard;
